import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi } from "@/lib/auth";
import { memoryApi } from "@/lib/memory";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null  = still checking session
  // false = unauthenticated
  // obj   = authenticated user
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) setUser(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const u = await authApi.login(email, password);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, name, security_questions, invite_token) => {
    const u = await authApi.register(email, password, name, security_questions, invite_token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore — we clear locally regardless
    }
    setUser(false);
  }, []);

  const setMemoryEnabled = useCallback(async (enabled) => {
    const result = await memoryApi.setSettings(enabled);
    setUser((u) => (u && typeof u === "object" ? { ...u, memory_enabled: !!result.memory_enabled } : u));
    return result;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, setMemoryEnabled }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
